<script setup>
import { computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useSpeckleStore } from '@haskoning/vue-speckle'

import SchematisationStageTabs from '@/components/schematisationDetail/SchematisationStageTabs.vue'
import StabSchematisationDialog from '@/components/schematisation/StabSchematisationDialog.vue'
import DikeTrajectoryCombinedDrawer from '@/components/dike/DikeTrajectoryCombinedDrawer.vue'
import SchematisationDuplicateDialog from '@/components/schematisation/SchematisationDuplicateDialog.vue'
import SchematisationExportFactSheetDialog from '@/components/schematisation/SchematisationExportFactSheetDialog.vue'
import SchematisationExportDStabilityDialog from '@/components/schematisation/SchematisationExportDStabilityDialog.vue'
import DeleteDialog from '@/components/project/DeleteDialog.vue'
import SchematisationExportJsonDialog from '@/components/schematisation/SchematisationExportJsonDialog.vue'
import { useSectionVisualisationStore } from '@/store/sectionVisualization'
import { useProjectSpeckleStore } from '@/store/projectSpeckle'

defineOptions({ name: 'SchematisationDetailView' })

const store = useStore()
const route = useRoute()
const speckleStore = useSpeckleStore()
const projectSpeckleStore = useProjectSpeckleStore()

const { currentSelection } = storeToRefs(speckleStore)

const currentProject = computed(() => store.state.project.currentProject)
const currentSchematisation = computed(() => store.state.stabSchematisation.currentItem)
const currentStabSchematisationStage = computed(
  () => store.state.stabSchematisationStage.currentItem
)
const stabSchematisationStageItems = computed(() => store.state.stabSchematisationStage.items)
const loading = computed(() => store.state.stabSchematisationStage.loading)
const currentDikeProfile = computed(() => store.state.dikeProfiles.currentItem)

const selectedSoilTypeRevision = computed(() => store.getters['project/selectedSoilTypeRevision'])

const toggleDrawer = (v) => store.dispatch('project/toggleDrawer', v)
const fetchStabSchematisation = (id) => store.dispatch('stabSchematisation/fetchItem', id)
const setCurrentStabSchematisationStage = (item) =>
  store.dispatch('stabSchematisationStage/setCurrentItem', item)
const fetchStabSchematisationStageItems = (id) =>
  store.dispatch('stabSchematisationStage/fetchItems', id)
const fetchDikeProfile = (id) => store.dispatch('dikeProfiles/fetchItem', id)
const fetchDikeSection = (id) => store.dispatch('dikeSection/fetchItem', id)
const fetchBaseSoilTypeItems = (id) => store.dispatch('baseSoilType/fetchItems', id)
const fetchItemsAdd = (id) => store.dispatch('soilType/fetchItemsAdd', id)

const getItem = (id) => {
  const parsed = id ? parseInt(id) : null
  return stabSchematisationStageItems.value.find((item) => item.id === parsed)
}

const fetchItems = async () => {
  await fetchStabSchematisation(route.params.schematisationId)
  await fetchStabSchematisationStageItems(route.params.schematisationId)
  await fetchDikeProfile(currentSchematisation.value.dike_profile)
  await fetchDikeSection(currentDikeProfile.value.properties.dikesection_id)
  await fetchBaseSoilTypeItems(route.params.id)
  await fetchItemsAdd(selectedSoilTypeRevision.value.id)
}

watch(stabSchematisationStageItems, () => {
  const item = getItem(route.params.schematisationStageId)
  if (item) {
    setCurrentStabSchematisationStage(item)
  } else if (stabSchematisationStageItems.value.length) {
    setCurrentStabSchematisationStage(stabSchematisationStageItems.value[0])
  }
})

watch(
  () => route.params.schematisationId,
  async () => {
    await fetchItems()
  }
)

onMounted(async () => {
  await toggleDrawer(false)
  if (!currentSelection.value?.version) {
    await speckleStore.getAllUserProjects()
    await projectSpeckleStore.fetchProjectSpeckle(route.params.id)
    await speckleStore.getSpeckleModels()
    await speckleStore.getSpeckleVersions()
  }

  await fetchItems()
})
</script>

<template>
  <v-card variant="flat">
    <v-progress-linear indeterminate v-if="loading" color="primary" />
    <SchematisationStageTabs
      v-if="currentStabSchematisationStage && selectedSoilTypeRevision"
      :key="`schematisation-tab-${currentStabSchematisationStage.id}`"
    />
  </v-card>
  <DikeTrajectoryCombinedDrawer /> <StabSchematisationDialog v-if="currentProject.id" />
  <SchematisationDuplicateDialog v-if="currentProject.id" />
  <SchematisationExportFactSheetDialog /> <SchematisationExportDStabilityDialog />
  <SchematisationExportJsonDialog />
  <DeleteDialog
    :state-key="'currentOverviewItem'"
    :store-name-space="'stabSchematisation'"
    property-name="name"
  />
</template>
